import {
  Component,
  ContentChild,
  Directive,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { SharedModule } from '@shared/shared.module';
import { DropFileComponent } from '@shared/components/drop-file/drop-file.component';
import { ModalConfirmationStd4SkinComponent } from '@shared/components/modal-confirmation-std4-skin/modal-confirmation-std4-skin.component';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { FileManagerService } from '@shared/service/file-manager.service';
import { Store } from '@ngxs/store';
import { BatchImportService } from '@shared/components/batch-import/batch-import.service';
import { finalize } from 'rxjs/operators';
import { ErrorNotification } from '@store/memo/memo.actions';
import { AlertService } from '@core/services/alert.service';
import { LoadingProgressBarComponent } from '@shared/components/loading-progress-bar/loading-progress-bar.component';
import { TranslateService } from '@ngx-translate/core';

@Directive({
  selector: '[appCustomButton]',
  standalone: true,
})
export class CustomButtonDirective {
  constructor(public templateRef: TemplateRef<unknown>) {}
}

@Component({
  selector: 'app-batch-import',
  standalone: true,
  templateUrl: './batch-import.component.html',
  styleUrls: ['./batch-import.component.scss'],
  imports: [
    CommonModule,
    SharedModule,
    DropFileComponent,
    ModalConfirmationStd4SkinComponent,
  ],
})
export class BatchImportComponent {
  @Input() type: string;
  @Input() params?: any;
  @Input() customFormData?: FormData;
  @Output() importSuccess = new EventEmitter<void>();
  @Output() importFailed = new EventEmitter<void>();

  isDownload = false;
  fileInput: File | null;
  progressTaskId: string;
  errorDetail: any;
  modal: NgbModalRef;

  @ContentChild(CustomButtonDirective)
  customButton: CustomButtonDirective;
  @ViewChild('importModal')
  importModal: ElementRef;
  @ViewChild('errorDetailModal')
  errorDetailModal: ElementRef;
  @ViewChild('progressBarModal')
  progressBarModal: LoadingProgressBarComponent;

  private modalService = inject(NgbModal);
  private apiService = inject(BatchImportService);
  private fileManager = inject(FileManagerService);
  private store = inject(Store);
  private alert = inject(AlertService);
  private translate = inject(TranslateService);

  openImportModal() {
    this.fileInput = null;
    this.openModal(this.importModal);
  }

  example() {
    this.isDownload = true;
    this.apiService
      .getExample(this.type, this.params)
      .pipe(finalize(() => (this.isDownload = false)))
      .subscribe(
        (res) => {
          const exampleFile = new Blob([res.body] as BlobPart[]);
          const filename = res.headers.get('x-file-name');
          this.fileManager.downloadFile(
            exampleFile,
            filename || 'import_example.xlsx',
          );
        },
        (error) => {
          this.store.dispatch(error);
        },
      );
  }

  uploadFile(file: File) {
    this.fileInput = file;
  }

  clearFile() {
    this.fileInput = null;
  }

  send() {
    const fd = this.customFormData
      ? this.customFormData
      : new FormData();
    fd.append('file', this.fileInput as Blob);
    this.apiService.import(fd, this.type).subscribe({
      next: (res) => {
        this.fileInput = null;
        this.progressTaskId = res.task_id;
        this.dismissAllModals();
        this.progressBarModal.showModal(res.task_id);
      },

      error: (error) => {
        this.store.dispatch(new ErrorNotification(error));
      },
    });
  }

  success() {
    this.dismissAllModals();
    this.alert.success(
      this.translate.instant('BATCH-IMPORT.IMPORT-SUCCESS'),
    );
    this.importSuccess.emit();
  }

  failed(error: any) {
    this.errorDetail = error.detail;
    this.dismissAllModals();
    this.openModal(this.errorDetailModal);
  }

  openModal(content: ElementRef) {
    if (this.modal) {
      this.modal.close();
    }
    this.modal = this.modalService.open(content, {
      centered: true,
      backdrop: 'static',
      size: 'size-smax56',
    });
  }

  isArray(value: string[]): boolean {
    return Array.isArray(value);
  }

  get fileSize() {
    return this.fileInput
      ? this.fileManager.getHumanFileSize(this.fileInput.size)
      : null;
  }

  private dismissAllModals(): void {
    try {
      this.modalService.dismissAll();
    } catch {}
  }
}
